// @LTG-ID: contact-step-category
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 980-1008, 587-606 (v4)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A1)
// @OWNER: web
//
// CHG-0083-M-A1 — Step 4: Category.
//
// 2x3 grid of category chips on desktop, single-column on phone (via CSS).
// 6 categories with distinct iconography (snapshot, membership, support,
// press, partnership, other). Active state: navy bg, gold icon, white text.
//
// Icons + labels + subs are ported verbatim from v4 source lines 587-606.
// Labels contain HTML entities (& → &amp;) preserved as plain `&` here since
// React handles entity decoding natively (the v4 source's `dangerouslySetInnerHTML`
// was Babel-standalone-defense; not needed in this proper React build).

export const CATEGORIES = [
    {
        key: "snapshot",
        label: "Strategic Snapshot",
        sub: "Question before ordering, or about a Snapshot already delivered.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M4 4h14l2 2v14H4z" />
                <path d="M8 9h8M8 13h8M8 17h5" />
            </svg>
        ),
    },
    {
        key: "membership",
        label: "Growth Membership",
        sub: "About the Growth Membership available after a Snapshot.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 3" />
            </svg>
        ),
    },
    {
        key: "support",
        label: "Existing customer",
        sub: "Already a Snapshot or Membership client. Need help.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M21 11a8 8 0 0 1-3.8 6.8L19 21l-3.7-1.6a8 8 0 1 1 5.7-8.4z" />
                <circle cx="9" cy="11" r="0.5" fill="currentColor" />
                <circle cx="12" cy="11" r="0.5" fill="currentColor" />
                <circle cx="15" cy="11" r="0.5" fill="currentColor" />
            </svg>
        ),
    },
    {
        key: "press",
        label: "Press \u0026 media",
        sub: "Interview, quote, commentary request from a journalist.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M7 9h6M7 13h6M7 17h4M17 9v8" />
            </svg>
        ),
    },
    {
        key: "partnership",
        label: "Partnership \u0026 referral",
        sub: "Banker, advisor, broker, attorney, or peer firm.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <circle cx="7" cy="9" r="3" />
                <circle cx="17" cy="9" r="3" />
                <path d="M2 19c0-2.8 2.2-5 5-5s5 2.2 5 5" />
                <path d="M12 19c0-2.8 2.2-5 5-5s5 2.2 5 5" />
            </svg>
        ),
    },
    {
        key: "other",
        label: "Other",
        sub: "Anything else. We will route it to the right person.",
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <circle cx="6" cy="12" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="18" cy="12" r="1.5" />
            </svg>
        ),
    },
];

export const StepCategory = ({ data, setData, onNext, onBack }) => {
    const first = (data.firstName || "").trim();
    return (
        <section
            className="ltgc-step"
            data-testid="contact-step-category"
            aria-labelledby="ltgc-step-category-heading"
        >
            <span className="ltgc-step-greeting">
                Step 4 · What it&apos;s about
            </span>
            <h2 id="ltgc-step-category-heading">
                {first}, <em>what&apos;s this about</em>?
            </h2>
            <p className="ltgc-sub">
                Pick the closest match. This routes your message to the right
                person at KGOB. You&apos;ll write the details on the next
                screen.
            </p>
            <div
                className="ltgc-cat-grid"
                role="radiogroup"
                aria-label="Inquiry category"
                data-testid="contact-cat-grid"
            >
                {CATEGORIES.map((c) => {
                    const active = data.category === c.key;
                    return (
                        <button
                            key={c.key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={
                                "ltgc-cat-chip " + (active ? "is-active" : "")
                            }
                            data-testid={`contact-cat-chip-${c.key}`}
                            onClick={() =>
                                setData({ ...data, category: c.key })
                            }
                        >
                            <span className="ltgc-cat-chip-icon">{c.icon}</span>
                            <span className="ltgc-cat-chip-label">
                                {c.label}
                            </span>
                            <span className="ltgc-cat-chip-sub">{c.sub}</span>
                        </button>
                    );
                })}
            </div>
            <div className="ltgc-step-nav">
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-ghost"
                    onClick={onBack}
                    data-testid="contact-step-category-back"
                >
                    ← Back
                </button>
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-primary"
                    disabled={!data.category}
                    onClick={onNext}
                    data-testid="contact-step-category-continue"
                >
                    Continue <span className="ltgc-arrow">→</span>
                </button>
            </div>
        </section>
    );
};

export default StepCategory;
